import React, { useRef, useCallback, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useTranslation } from 'react-i18next';
import { forceCollide, forceRadial, forceX, forceY, forceCenter } from 'd3-force-3d';
import { getSharedProjects } from '../services/apiService';
import { Dialog as ShadcnDialog, DialogContent } from '@/components/ui/dialog';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR, CENTER_NODE_COLOR, COLORS, COMMUNITY_COLORS, COMMUNITY_STROKE_COLORS, getPhysicsPreset } from './graph/graphConfig';
import { useGraphInteraction } from './graph/useGraphInteraction';
import { renderNode, getNodeRadius } from './graph/nodeRenderers';
import { getCollaborationLinkColor, getCollaborationLinkWidth } from './graph/linkRenderers';
import { detectCommunities, convexHull } from './graph/graphUtils';
import GraphTooltip from './graph/GraphTooltip';
import GraphLegend from './graph/GraphLegend';
import GraphStatsFooter from './graph/GraphStatsFooter';
import SharedProjectsDialog from './graph/SharedProjectsDialog';
import FullscreenButton from './graph/FullscreenButton';
import type { CollaborationGraphResponse, SharedProject } from '../types/api';
import type { NodeId } from '../types/graph';

const NODE_RADIUS_BASE = 2;
const NODE_RADIUS_MULTIPLIER = 1.0;
const CENTER_NODE_RADIUS = 14;
const LABEL_FONT_SIZE = 5;
const SHOW_LABELS_IN_OVERVIEW = true;
const KEY_COLLABORATOR_MIN_DEGREE = 5;

interface CollaborationGraphModalProps {
  data: CollaborationGraphResponse;
  centerPersonName: string;
  onClose: () => void;
  onNodeClick: (nodeId: number) => void;
  onProjectClick: (projectId: number) => void;
}

interface InternalNode {
  id: number;
  name: string;
  size: number;
  color: string;
  faculty: string;
  facultyAbbr: string;
  isCenter: boolean;
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface InternalLink {
  source: number | any;
  target: number | any;
  value: number;
  title: string;
  _isCenterLink?: boolean;
  _belowThreshold?: boolean;
}

interface NodePosition {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export default function CollaborationGraphModal({ data, centerPersonName, onClose, onNodeClick, onProjectClick }: CollaborationGraphModalProps) {
  const { t } = useTranslation('person');
  const graphRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<Record<number, NodePosition>>({});
  const communitiesRef = useRef<Map<NodeId, number>>(new Map());
  const hullsDrawnThisFrameRef = useRef<boolean>(false);

  const maxWeight: number = data.stats?.maxEdgeWeight || 1;
  const centerNodeId = useMemo<number | null>(() => {
    const cn = data.nodes.find(n => n.isCenter);
    return cn ? cn.id : null;
  }, [data.nodes]);

  const defaultMinWeight: number = Math.max(1, Math.floor(maxWeight * 0.3));
  const [minEdgeWeight, setMinEdgeWeight] = useState<number>(defaultMinWeight);

  const totalCollaborators: number = data.nodes.filter(n => !n.isCenter).length;
  const maxDisplayNodes: number = totalCollaborators;
  const [nodeCount, setNodeCount] = useState<number>(Math.min(30, totalCollaborators));

  const [selectedLink, setSelectedLink] = useState<InternalLink | null>(null);
  const [sharedProjects, setSharedProjects] = useState<SharedProject[]>([]);
  const [sharedProjectsLoading, setSharedProjectsLoading] = useState<boolean>(false);

  const handleNodeClick = useCallback((node: any) => {
    if (node && node.id) onNodeClick(node.id);
  }, [onNodeClick]);

  const handleLinkClick = useCallback(async (link: any) => {
    if (!link) return;
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;

    setSelectedLink(link);
    setSharedProjects([]);
    setSharedProjectsLoading(true);
    try {
      const res = await getSharedProjects(sourceId, targetId);
      setSharedProjects(res.projects || []);
    } catch (err) {
      console.error('Failed to fetch shared projects:', err);
      setSharedProjects([]);
    } finally {
      setSharedProjectsLoading(false);
    }
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  const activeFaculties = useMemo<string[]>(() => {
    const unique = [...new Set(data.nodes.filter(n => !n.isCenter).map(n => n.facultyAbbr))];
    return unique.filter((abbr): abbr is string => abbr != null && !!FACULTY_COLORS[abbr]);
  }, [data.nodes]);

  // Faculty angle map for clustering force
  const facultyAngleMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    activeFaculties.forEach((f, i) => {
      map[f] = (2 * Math.PI * i) / Math.max(activeFaculties.length, 1);
    });
    return map;
  }, [activeFaculties]);

  // Build graph nodes — take center + top nodeCount collaborators.
  // data.nodes comes from backend sorted by shared_projects_count DESC (center first).
  const graphNodes = useMemo<InternalNode[]>(() => {
    let collabCount = 0;
    const result: InternalNode[] = [];
    for (const node of data.nodes) {
      if (node.isCenter) {
        const abbr = node.facultyAbbr || node.faculty;
        result.push({
          id: node.id, name: node.label, size: 300,
          color: CENTER_NODE_COLOR, faculty: node.faculty, facultyAbbr: abbr,
          isCenter: true, fx: 0, fy: 0,
        });
      } else if (collabCount < nodeCount) {
        collabCount++;
        const abbr = node.facultyAbbr || node.faculty;
        result.push({
          id: node.id, name: node.label, size: node.size * 0.3,
          color: FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR,
          faculty: node.faculty, facultyAbbr: abbr, isCenter: false,
        });
      }
    }
    return result;
  }, [data.nodes, nodeCount]);

  // Raw edge data (never mutated by the library)
  const rawEdges = useMemo(() => {
    const edges = data.edges.map(edge => ({
      source: edge.source,
      target: edge.target,
      value: edge.value,
      title: edge.title,
    }));
    edges.sort((a, b) => (b.value || 0) - (a.value || 0));
    return edges;
  }, [data.edges]);

  // Build graphData: center edges always present, mutual edges filtered by threshold.
  // Each link gets `_isCenterLink` and `_belowThreshold` flags for rendering.
  const graphData = useMemo(() => {
    const nodeIdSet = new Set(graphNodes.map(n => n.id));
    const links: InternalLink[] = [];
    for (const e of rawEdges) {
      if (!nodeIdSet.has(e.source) || !nodeIdSet.has(e.target)) continue;
      const isCenterLink = e.source === centerNodeId || e.target === centerNodeId;
      if (isCenterLink) {
        links.push({
          source: e.source, target: e.target, value: e.value, title: e.title,
          _isCenterLink: true,
          _belowThreshold: e.value < minEdgeWeight,
        });
      } else if (e.value >= minEdgeWeight) {
        links.push({
          source: e.source, target: e.target, value: e.value, title: e.title,
          _isCenterLink: false,
          _belowThreshold: false,
        });
      }
    }

    const positions = positionsRef.current;
    const nodesWithPositions = graphNodes.map(n => ({
      ...n,
      ...(positions[n.id] ? {
        x: positions[n.id].x,
        y: positions[n.id].y,
        vx: positions[n.id].vx || 0,
        vy: positions[n.id].vy || 0,
      } : {}),
    }));
    return { nodes: nodesWithPositions, links };
  }, [graphNodes, rawEdges, minEdgeWeight, centerNodeId]);

  const interaction = useGraphInteraction(containerRef, graphData.links as any);

  // Key collaborators: 5+ shared outcomes with the center person
  const keyCollaborators = useMemo<Set<number>>(() => {
    const keys = new Set<number>();
    for (const e of rawEdges) {
      if (e.value >= KEY_COLLABORATOR_MIN_DEGREE) {
        if (e.source === centerNodeId) keys.add(e.target);
        else if (e.target === centerNodeId) keys.add(e.source);
      }
    }
    return keys;
  }, [rawEdges, centerNodeId]);

  // Community detection
  useEffect(() => {
    communitiesRef.current = detectCommunities(graphData.nodes as any, graphData.links as any);
  }, [graphData.nodes, graphData.links]);

  // Container size tracking — drives explicit ForceGraph2D dimensions and
  // triggers a simulation reheat once the dialog has finished animating in.
  // Measured synchronously via useLayoutEffect (offsetWidth ignores the dialog's
  // CSS scale animation, so we get the true layout size before first paint),
  // and kept up to date by a ResizeObserver.
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    setContainerSize({ w: el.offsetWidth, h: el.offsetHeight });
  }, []);
  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const el = containerRef.current;
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize(prev => {
          const w = Math.round(width);
          const h = Math.round(height);
          if (prev.w === w && prev.h === h) return prev;
          return { w, h };
        });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Configure d3 forces
  useEffect(() => {
    const fg = graphRef.current;
    if (!fg) return;
    const nodeCount = graphData.nodes.length;
    const preset = getPhysicsPreset(nodeCount);

    // Scale linkDistance to actual canvas size so the natural layout fills the
    // viewport at zoom=1 (no zoomToFit needed). Falls back to the preset value
    // when dimensions are not yet known.
    const canvasMin = Math.min(containerSize.w || 0, containerSize.h || 0);
    const linkDistance = canvasMin > 0
      ? Math.max(preset.linkDistance, canvasMin / Math.max(6, Math.sqrt(nodeCount) * 1.6))
      : preset.linkDistance;

    fg.d3Force('charge').strength(preset.chargeStrength);

    fg.d3Force('link')
      .distance((link: any) => {
        const t = Math.min((link.value || 1) / maxWeight, 1);
        return linkDistance * (1.5 - t);
      })
      .strength(preset.linkStrength);

    fg.d3Force('collide', forceCollide()
      .radius((node: any) => {
        if (node.isCenter) return CENTER_NODE_RADIUS + 8;
        return getNodeRadius(node.size, NODE_RADIUS_BASE, NODE_RADIUS_MULTIPLIER) + 8;
      })
      .strength(0.7)
      .iterations(2));

    fg.d3Force('radial', forceRadial(
      (node: any) => node.isCenter ? 0 : linkDistance,
      0, 0
    ).strength((node: any) => node.isCenter ? 0 : 0.05));

    fg.d3Force('clusterX', forceX()
      .x((node: any) => {
        if (node.isCenter) return 0;
        const angle = facultyAngleMap[node.facultyAbbr] || 0;
        return Math.cos(angle) * linkDistance * 0.4;
      })
      .strength(0.03));

    fg.d3Force('clusterY', forceY()
      .y((node: any) => {
        if (node.isCenter) return 0;
        const angle = facultyAngleMap[node.facultyAbbr] || 0;
        return Math.sin(angle) * linkDistance * 0.4;
      })
      .strength(0.03));

    fg.d3Force('centerForce', forceCenter(0, 0).strength(0.02));
  }, [graphData.nodes.length, maxWeight, facultyAngleMap, containerSize.w, containerSize.h]);

  // Documented react-force-graph pattern for deferred sizing: keep the engine
  // alive (long cooldownTime) and reheat the simulation when dimensions change.
  // The simulation then re-runs against the real canvas and the layout settles
  // properly without any zoomToFit call.
  useEffect(() => {
    if (!containerSize.w || !containerSize.h) return;
    const fg = graphRef.current;
    if (!fg) return;
    fg.d3ReheatSimulation();
  }, [containerSize.w, containerSize.h]);

  // Workaround: 500 ms after mount, programmatically nudge the node-count
  // slider by one and snap it back. The slider-change effect already reheats
  // the simulation against real dimensions and the layout settles correctly —
  // mimicking the manual nudge that fixed the view in earlier iterations.
  useEffect(() => {
    const timer = setTimeout(() => {
      setNodeCount(c => {
        const direction = c > 1 ? -1 : 1;
        setTimeout(() => setNodeCount(c2 => c2 - direction), 80);
        return c + direction;
      });
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRender = useRef<boolean>(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const fg = graphRef.current;
    if (!fg) return;
    fg.d3ReheatSimulation();
  }, [minEdgeWeight, nodeCount]);

  // Draw community hulls on canvas (called before first node each frame)
  const drawCommunityHulls = useCallback((ctx: CanvasRenderingContext2D) => {
    const communities = communitiesRef.current;
    if (!communities.size) return;

    const groups: Record<number, Array<{ x: number; y: number }>> = {};
    for (const node of graphData.nodes) {
      const c = communities.get(node.id);
      if (c == null || node.x == null || node.y == null) continue;
      if (!groups[c]) groups[c] = [];
      groups[c].push({ x: node.x, y: node.y });
    }

    Object.entries(groups).forEach(([commId, pts]) => {
      if (pts.length < 3) return;
      const hull = convexHull(pts);
      if (hull.length < 3) return;

      const colorIdx = Number(commId) % COMMUNITY_COLORS.length;
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.fillStyle = COMMUNITY_COLORS[colorIdx];
      ctx.beginPath();

      // Draw rounded hull with padding
      const pad = 30;
      const expanded = hull.map(p => {
        const cx = pts.reduce((s, q) => s + q.x, 0) / pts.length;
        const cy = pts.reduce((s, q) => s + q.y, 0) / pts.length;
        const dx = p.x - cx;
        const dy = p.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        return { x: p.x + (dx / dist) * pad, y: p.y + (dy / dist) * pad };
      });

      ctx.moveTo(expanded[0].x, expanded[0].y);
      for (let i = 1; i < expanded.length; i++) {
        ctx.lineTo(expanded[i].x, expanded[i].y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = COMMUNITY_STROKE_COLORS[colorIdx];
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    });
  }, [graphData.nodes]);

  return (
    <ShadcnDialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[1400px] w-[90%] h-[85vh] max-h-[900px] flex flex-col overflow-hidden p-0" ref={wrapperRef}>
        {/* Header */}
        <div className="px-8 pt-6 pb-5 border-b border-ctu-border bg-background">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
              {t('collaborationGraph.title')}
            </span>
          </div>
          <h2 className="text-xl font-semibold m-0 text-ctu-text leading-tight">
            {centerPersonName}
          </h2>
          <div className="text-xs mt-1 text-ctu-text-muted">
            {t('collaborationGraph.clickPerson')}
          </div>
        </div>

        {/* Stats bar */}
        <GraphStatsFooter stats={[
          { label: t('collaborationGraph.collaborators'), value: `${graphData.nodes.length - 1} / ${totalCollaborators}` },
          { label: t('collaborationGraph.connections'), value: graphData.links.length },
        ]} />

        {/* Graph area */}
        <div
          ref={containerRef}
          onMouseMove={interaction.handleMouseMove as any}
          className="flex-1 relative bg-muted/20 overflow-hidden"
        >
          <FullscreenButton targetRef={wrapperRef} />

          <GraphLegend
            title={t('collaborationGraph.legend.title')}
            items={[
              { key: '__center__', label: centerPersonName, color: CENTER_NODE_COLOR },
              ...activeFaculties.map(faculty => ({
                key: faculty,
                label: faculty,
                color: FACULTY_COLORS[faculty],
              })),
            ]}
            minEdgeValue={minEdgeWeight}
            maxEdgeValue={maxWeight}
            onMinEdgeValueChange={setMinEdgeWeight}
            filterLabel={t('collaborationGraph.edgeFilter.label') || 'Min. shared results'}
            secondSliderValue={nodeCount}
            secondSliderMin={1}
            secondSliderMax={maxDisplayNodes}
            onSecondSliderChange={setNodeCount}
            secondSliderLabel={t('collaborationGraph.nodeCount.label') || 'Number of collaborators'}
            t={t}
          >
            <div
              className="flex items-center gap-1.5 mt-2 pt-2 text-xs border-t border-border text-muted-foreground"
            >
              <span className="text-amber-500 text-sm leading-none">&#9733;</span>
              <span>{t('collaborationGraph.keyCollaborator') || 'Key collaborator (5+ connections)'}</span>
            </div>
          </GraphLegend>

          <ForceGraph2D
            ref={graphRef}
            width={containerSize.w || undefined}
            height={containerSize.h || undefined}
            graphData={graphData}
            nodeId="id"
            nodeVal={(node: any) => node.isCenter ? CENTER_NODE_RADIUS : getNodeRadius(node.size, NODE_RADIUS_BASE, NODE_RADIUS_MULTIPLIER) / 3}
            nodeColor={(node: any) => node.color}
            nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D) => {
              // Draw community hulls once per frame (before first node)
              if (!hullsDrawnThisFrameRef.current) {
                hullsDrawnThisFrameRef.current = true;
                drawCommunityHulls(ctx);
                requestAnimationFrame(() => { hullsDrawnThisFrameRef.current = false; });
              }

              const nodeRadius = node.isCenter
                ? CENTER_NODE_RADIUS
                : getNodeRadius(node.size, NODE_RADIUS_BASE, NODE_RADIUS_MULTIPLIER);

              const highlighted = interaction.isNodeHighlighted(node.id);
              renderNode(node, ctx, {
                lodTier: 'detail',
                isHighlighted: highlighted,
                hasHover: interaction.hasHover,
                isHovered: interaction.hoveredNode?.id === node.id,
                visibleLabels: null,
                color: node.color,
                radius: nodeRadius,
                label: node.isCenter ? null : node.name,
                bbox: null,
                isCenter: node.isCenter,
                isKeyCollaborator: keyCollaborators.has(node.id),
                fontSize: LABEL_FONT_SIZE,
                showLabelsInOverview: SHOW_LABELS_IN_OVERVIEW,
              });
            }}
            linkWidth={(link: any) => {
              if (interaction.hoveredLink === link) {
                const ceiling = Math.max(maxWeight, 15);
                const t = Math.min((link.value || 1) / ceiling, 1);
                return (1.0 + t * 8.5) * 2;
              }
              if (link._belowThreshold) {
                return interaction.hasHover && !interaction.isLinkHighlighted(link) ? 0.4 : 1.0;
              }
              return getCollaborationLinkWidth(link, 'value', maxWeight, interaction.isLinkHighlighted(link), interaction.hasHover);
            }}
            linkColor={(link: any) => {
              if (interaction.hoveredLink === link) {
                const sourceNode = typeof link.source === 'object' ? link.source : null;
                return sourceNode?.color || COLORS.primary;
              }
              const highlighted = interaction.isLinkHighlighted(link);

              // Center links below threshold: always neutral
              if (link._belowThreshold) {
                if (interaction.hasHover && !highlighted) return 'rgba(180, 180, 190, 0.12)';
                return 'rgba(180, 180, 190, 0.22)';
              }

              // Center links not highlighted during hover: subtle grey so they stay visible
              if (link._isCenterLink && interaction.hasHover && !highlighted) {
                return 'rgba(160, 160, 175, 0.18)';
              }

              const hoveredId = interaction.hoveredNode?.id;
              let edgeColor: string | undefined;
              if (hoveredId && highlighted) {
                const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                const otherNode = sourceId === hoveredId ? link.target : link.source;
                edgeColor = typeof otherNode === 'object' ? otherNode.color : DEFAULT_NODE_COLOR;
              }
              return getCollaborationLinkColor(link, 'value', maxWeight, highlighted, interaction.hasHover, edgeColor);
            }}
            linkCurvature={0.15}
            linkCanvasObjectMode="after"
            linkCanvasObject={(link: any, ctx: CanvasRenderingContext2D) => {
              if (interaction.hoveredLink !== link) return;
              const src = typeof link.source === 'object' ? link.source : null;
              const tgt = typeof link.target === 'object' ? link.target : null;
              if (!src || !tgt || src.x == null || tgt.x == null) return;

              const mx = (src.x + tgt.x) / 2;
              const my = (src.y + tgt.y) / 2;
              const label = `${link.value}`;

              ctx.save();
              ctx.font = 'bold 5px Sans-Serif';
              const textW = ctx.measureText(label).width;
              const padX = 3;
              const padY = 2;

              ctx.fillStyle = 'rgba(30, 41, 59, 0.85)';
              const bx = mx - textW / 2 - padX;
              const by = my - 4 - padY;
              const bw = textW + padX * 2;
              const bh = 8 + padY * 2;
              const rr = 3;
              ctx.beginPath();
              ctx.moveTo(bx + rr, by);
              ctx.lineTo(bx + bw - rr, by);
              ctx.arcTo(bx + bw, by, bx + bw, by + rr, rr);
              ctx.lineTo(bx + bw, by + bh - rr);
              ctx.arcTo(bx + bw, by + bh, bx + bw - rr, by + bh, rr);
              ctx.lineTo(bx + rr, by + bh);
              ctx.arcTo(bx, by + bh, bx, by + bh - rr, rr);
              ctx.lineTo(bx, by + rr);
              ctx.arcTo(bx, by, bx + rr, by, rr);
              ctx.closePath();
              ctx.fill();

              ctx.fillStyle = '#fff';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(label, mx, my);
              ctx.restore();
            }}
            linkVisibility={(link: any) => {
              if (link._isCenterLink) return true;
              const highlighted = interaction.isLinkHighlighted(link);
              if (highlighted) return true;
              if (interaction.hasHover && !highlighted) return false;
              return true;
            }}
            linkHoverPrecision={8}
            onNodeClick={handleNodeClick}
            onNodeHover={interaction.handleNodeHover}
            onLinkClick={handleLinkClick}
            onLinkHover={interaction.handleLinkHover}
            nodeLabel=""
            linkLabel=""
            warmupTicks={100}
            cooldownTime={4000}
            d3AlphaDecay={0.03}
            d3VelocityDecay={0.3}
            onEngineTick={() => {
              const pos: Record<number, NodePosition> = {};
              for (const n of graphData.nodes) {
                if (n.x != null) pos[n.id] = { x: n.x!, y: n.y!, vx: (n as any).vx || 0, vy: (n as any).vy || 0 };
              }
              positionsRef.current = pos;
            }}
            enableNodeDrag={true}
            enableZoomInteraction={true}
            enablePanInteraction={true}
          />
        </div>

        {/* Tooltip */}
        {!selectedLink && (
          <GraphTooltip
            visible={!!(interaction.hoveredNode || interaction.hoveredLink)}
            x={interaction.tooltipPos.x}
            y={interaction.tooltipPos.y}
          >
            {interaction.hoveredNode ? (
              <>
                <strong>{interaction.hoveredNode.name}</strong>
                <br />
                <span className="text-[0.8em] opacity-90">
                  {interaction.hoveredNode.facultyAbbr || interaction.hoveredNode.faculty}
                </span>
              </>
            ) : interaction.hoveredLink ? (
              <>
                <strong>{typeof interaction.hoveredLink.source === 'object' ? interaction.hoveredLink.source.name : interaction.hoveredLink.source}</strong>
                {` ${t('collaborationGraph.sharedProjects.and')} `}
                <strong>{typeof interaction.hoveredLink.target === 'object' ? interaction.hoveredLink.target.name : interaction.hoveredLink.target}</strong>
                <br />
                <span className="text-[0.8em] opacity-90">
                  {interaction.hoveredLink.value} {t('collaborationGraph.sharedOutcomes')}
                </span>
              </>
            ) : null}
          </GraphTooltip>
        )}

        {/* Shared Projects Dialog */}
        <SharedProjectsDialog
          selectedLink={selectedLink}
          sharedProjects={sharedProjects}
          sharedProjectsLoading={sharedProjectsLoading}
          onClose={() => setSelectedLink(null)}
          onProjectClick={onProjectClick}
          t={t}
        />
      </DialogContent>
    </ShadcnDialog>
  );
}
